import type { MutableRefObject, RefObject } from "react";
import { useEffect, useEffectEvent, useLayoutEffect } from "react";

import type { ReplyErrorState } from "../OnboardingAgentPanel.types";

type UseOnboardingAgentPanelAutoScrollParams = {
    readonly isPanelOpen: boolean;
    /** Count of user messages — changes only when the user sends, not when an assistant reply is appended. */
    readonly userMessagesLength: number;
    readonly replyError: ReplyErrorState | null;
    readonly scrollContainerRef: RefObject<HTMLDivElement | null>;
    readonly scrollContentRef: RefObject<HTMLElement | null>;
    readonly scrollSpacerRef: RefObject<HTMLDivElement | null>;
    readonly lastUserMessageRef: RefObject<HTMLDivElement | null>;
    readonly isFirstOpenScrollRef: MutableRefObject<boolean>;
};

type AnchoredMeasurements = {
    readonly container: HTMLDivElement;
    readonly spacer: HTMLDivElement | null;
    readonly userMessageOffsetTop: number;
    readonly requiredSpacer: number;
};

export const useOnboardingAgentPanelAutoScroll = ({
    isPanelOpen,
    userMessagesLength,
    replyError,
    scrollContainerRef,
    scrollContentRef,
    scrollSpacerRef,
    lastUserMessageRef,
    isFirstOpenScrollRef,
}: UseOnboardingAgentPanelAutoScrollParams): void => {
    const readAnchoredMeasurements = (): AnchoredMeasurements | null => {
        const container = scrollContainerRef.current;
        const spacer = scrollSpacerRef.current;
        const userMessage = lastUserMessageRef.current;

        if (!container || isFirstOpenScrollRef.current || !userMessage) {
            return null;
        }

        const userMessageRect = userMessage.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();
        const userMessageOffsetTop = userMessageRect.top - containerRect.top + container.scrollTop;

        const currentSpacerHeight = spacer ? spacer.offsetHeight : 0;
        const naturalContentHeight = container.scrollHeight - currentSpacerHeight;
        const heightBelowUserMessage = Math.max(0, naturalContentHeight - userMessageOffsetTop);
        const requiredSpacer = Math.max(0, container.clientHeight - heightBelowUserMessage);

        return {
            container,
            spacer,
            userMessageOffsetTop,
            requiredSpacer,
        };
    };

    const applyAnchoredSpacerOnly = useEffectEvent((): void => {
        const measurements = readAnchoredMeasurements();
        if (!measurements) {
            return;
        }

        if (measurements.spacer) {
            measurements.spacer.style.height = `${measurements.requiredSpacer}px`;
        }
    });

    const applyAutoScroll = useEffectEvent((): void => {
        const container = scrollContainerRef.current;
        if (!container) {
            return;
        }
        const spacer = scrollSpacerRef.current;
        const userMessage = lastUserMessageRef.current;

        if (isFirstOpenScrollRef.current || !userMessage) {
            if (spacer) {
                spacer.style.height = "0px";
            }
            container.scrollTop = Math.max(0, container.scrollHeight - container.clientHeight);
            return;
        }

        const measurements = readAnchoredMeasurements();
        if (!measurements) {
            return;
        }

        if (measurements.spacer) {
            measurements.spacer.style.height = `${measurements.requiredSpacer}px`;
        }

        measurements.container.scrollTop = Math.max(0, measurements.userMessageOffsetTop);
    });

    useLayoutEffect(() => {
        if (!isPanelOpen) {
            return;
        }
        applyAutoScroll();
    }, [isPanelOpen, userMessagesLength, replyError]);

    useEffect(() => {
        if (!isPanelOpen) {
            return;
        }
        const content = scrollContentRef.current;
        if (!content || typeof ResizeObserver === "undefined") {
            return;
        }
        const observer = new ResizeObserver(() => {
            if (isFirstOpenScrollRef.current || !lastUserMessageRef.current) {
                applyAutoScroll();
                return;
            }
            applyAnchoredSpacerOnly();
        });
        observer.observe(content);

        return () => observer.disconnect();
    }, [isPanelOpen]);
};
